import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@/lib/notifications';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { useAuthStore } from '../../store/authStore';
import photoCollectorService from '../../services/photoCollectorService';
import PhotoCollectorPanel from '../../pages/user/generate-image/PhotoCollectorPanel';

/**
 * PhotoBoothInbox — reusable wrapper around the generate-image PhotoCollectorPanel
 * so the customer photo-booth queue can appear on ANY editor page (manual editor,
 * bulk edit, AI templates), not just generate-image.
 *
 * It owns everything the panel needs so a host page only has to say what to DO with
 * a picked photo:
 *  - resolves the current office space (admins have none) and whether the booth is
 *    ENABLED for it — renders NOTHING unless the booth is active for this user;
 *  - derives the shop identity for the QR poster;
 *  - on pick, fetches the full (uncropped) photo — or the PDF — wraps it in a File and hands it to
 *    `onPickFile(file, uploadId, meta)`.
 *
 * Props:
 *   onPickFile(file, uploadId, meta) — REQUIRED. Receives the chosen photo (or PDF) as a File.
 *     meta = { kind: 'image' | 'file', isPrivate, name, pageCount } — pageCount: a PDF's pages (null for a photo); isPrivate: the customer asked for it to be deleted once it has
 *     been printed / used, so the host reports it with reportBoothDone (utils/boothPrivacy.js) when the work is done.
 *   kinds — what this host can work with: ['image'] (default — every photo editor shows photos only) or ['image', 'file']
 *     to list the PDF files the customers send too (Document Print).
 *   fileName — optional File name for a PHOTO (default 'booth-photo.jpg'); a PDF keeps the name its sender gave it.
 *   refreshKey — optional; bump to force the queue to re-fetch.
 *   className — optional wrapper classes (e.g. a card). Applied ONLY when the panel
 *     actually renders, so a disabled booth never leaves an empty wrapper behind.
 *   onStatusChange(enabled) — optional; fired once the booth's on/off state resolves
 *     (false when there's no office space). Lets the host hide a fallback uploader
 *     (e.g. the QR transfer card) while the booth is active.
 *   collapsible — optional; adds a fold-away chevron to the panel (open by default).
 *   multiSelect + onPickFiles(files, ids, metas) — optional multi-pick mode (bulk edit):
 *     taps select photos and a সিলেক্ট button delivers ALL of them as Files at once
 *     (files[i] pairs with ids[i] and metas[i]).
 *   markedIds — booth ids the host already took; shown checked, not re-selectable.
 */
// One shared array so a host that does not pass `kinds` never hands the panel a new one on every render.
const PHOTOS_ONLY = ['image'];

// A queue item → the File a host works with. A photo is fetched as the JPEG the booth keeps; a PDF keeps its sender's (sanitized) name.
async function fetchBoothFile(uploadId, item, photoName) {
    if (item?.kind === 'file') {
        const blob = await photoCollectorService.fileBlob(uploadId);
        return new File([blob], item.originalName || 'document.pdf', { type: 'application/pdf' });
    }
    const blob = await photoCollectorService.imageBlob(uploadId, false);
    return new File([blob], photoName, { type: blob.type || 'image/jpeg' });
}
const metaOf = (item) => ({ kind: item?.kind === 'file' ? 'file' : 'image', isPrivate: item?.isPrivate === true, name: item?.originalName || '', pageCount: item?.pageCount ?? null });

export default function PhotoBoothInbox({ onPickFile, onPickFiles, multiSelect = false, markedIds, fileName = 'booth-photo.jpg', refreshKey = 0, className = '', onStatusChange, collapsible = false, kinds = PHOTOS_ONLY }) {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const { selectedOfficeSpace: rawSelectedOfficeSpace } = useOfficeSpaceStore();
    const isAdmin = user?.role === 'admin';
    const officeSpace = isAdmin ? null : rawSelectedOfficeSpace;
    const officeSpaceId = officeSpace?.id || null;

    // Is the photo booth switched ON for this office space? Until resolved (and when
    // disabled or there's no office space) the panel stays hidden. The status callback
    // rides in a ref so an inline arrow prop never re-triggers the fetch.
    const [enabled, setEnabled] = useState(false);
    const statusRef = useRef(onStatusChange);
    statusRef.current = onStatusChange;
    useEffect(() => {
        let alive = true;
        const report = (en) => { setEnabled(en); statusRef.current?.(en); };
        if (!officeSpaceId) { report(false); return; }
        photoCollectorService.get(officeSpaceId)
            .then((res) => { if (alive) report(res?.data?.collector?.enabled === true); })
            .catch(() => { if (alive) report(false); });
        return () => { alive = false; };
    }, [officeSpaceId, refreshKey]);

    if (!officeSpaceId || !enabled) return null;

    // Fetch the full photo (or PDF), wrap as a File, hand off to the host page.
    const handlePick = async (uploadId, item) => {
        try {
            const file = await fetchBoothFile(uploadId, item, fileName);
            await onPickFile?.(file, uploadId, metaOf(item));
        } catch {
            notifications.show({
                title: t('photoCollectorPanel.loadFailedTitle'),
                message: t('photoCollectorPanel.loadFailed'),
                color: 'red',
            });
        }
    };

    // Multi-pick: fetch every chosen photo, deliver them together. Failed fetches are
    // dropped (with one warning) rather than sinking the whole batch; files[i] always
    // pairs with the returned ids[i] so hosts can track which booth photo became which File.
    const handlePickMany = async (ids, items = []) => {
        const pairs = (await Promise.all(ids.map(async (id, i) => {
            try {
                return { id, file: await fetchBoothFile(id, items[i], `booth-photo-${i + 1}.jpg`), meta: metaOf(items[i]) };
            } catch { return null; }
        }))).filter(Boolean);
        if (pairs.length < ids.length) {
            notifications.show({
                title: t('photoCollectorPanel.loadFailedTitle'),
                message: t('photoCollectorPanel.loadFailed'),
                color: 'red',
            });
        }
        if (pairs.length) await onPickFiles?.(pairs.map(p => p.file), pairs.map(p => p.id), pairs.map(p => p.meta));
    };

    const panel = (
        <PhotoCollectorPanel
            officeSpaceId={officeSpaceId}
            shopName={officeSpace?.businessProfile?.shopName || officeSpace?.name}
            shopLogo={officeSpace?.businessProfile?.shopLogo}
            shopPhone={officeSpace?.businessProfile?.shopPhone}
            shopAddress={officeSpace?.businessProfile?.businessAddress}
            onPick={handlePick}
            refreshKey={refreshKey}
            collapsible={collapsible}
            selectMode={multiSelect}
            onSelectMany={handlePickMany}
            markedIds={markedIds}
            kinds={kinds}
        />
    );
    return className ? <div className={className}>{panel}</div> : panel;
}
